/**
 * PowerCanvas v2 — Visual canvas for PDU → Server power cabling.
 *
 * Interaction:
 *  1. Click a FREE outlet on a PDU  → outlet highlighted orange (pending source)
 *  2. Click a FREE PSU on a server  → connection form popup → Save
 *  3. Click a CONNECTED outlet/PSU  → disconnect confirm popup
 *  4. Escape / "Cancelar"           → cancel pending selection
 *
 * Views:
 *  - Lista    (default): Two independently scrollable columns.
 *  - Diagrama           : SVG wiring diagram with bezier curves + arrowheads.
 */

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import LocationFilter, { matchesLocation } from './LocationFilter';
import PowerMapView from './PowerMapView';
import api from '../services/api';

// ── constants ─────────────────────────────────────────────────────────────────

const CABLE_TYPES = [
  'IEC C13/C14',
  'IEC C19/C20',
  'Schuko CEE7/4',
  'IEC 309 (32A)',
  'IEC 309 (63A)',
  'Autre',
];

const CABLE_COLORS = [
  { value: 'negro',    label: 'Negro',    hex: '#1f2937' },
  { value: 'gris',     label: 'Gris',     hex: '#9ca3af' },
  { value: 'blanco',   label: 'Blanco',   hex: '#e5e7eb' },
  { value: 'rojo',     label: 'Rojo',     hex: '#ef4444' },
  { value: 'azul',     label: 'Azul',     hex: '#3b82f6' },
  { value: 'verde',    label: 'Verde',    hex: '#22c55e' },
  { value: 'amarillo', label: 'Amarillo', hex: '#eab308' },
  { value: 'naranja',  label: 'Naranja',  hex: '#f97316' },
];

function colorHex(name) {
  return CABLE_COLORS.find(c => c.value === name)?.hex || '#9ca3af';
}

// ── sub-components ────────────────────────────────────────────────────────────

/** Single outlet row in a PDU/SAI card */
function OutletRow({ outlet, source, isSelected, pendingOutlet, onSelect }) {
  const conn        = outlet.connection;
  const childPduId  = outlet.child_pdu_id;
  const childPduName= outlet.child_pdu_name;
  const isCurrentlyPending = isSelected;

  let bg     = 'var(--bg-toolbar)';
  let border = 'var(--slate-300)';
  let cursor = 'pointer';

  if (isCurrentlyPending) {
    bg = 'var(--port-selected-bg)'; border = 'var(--port-selected-bd)';
  } else if (conn) {
    const cHex = colorHex(conn.cable_color);
    bg = `${cHex}18`;
    border = cHex;
  } else if (childPduId) {
    bg = 'var(--amber-50)'; border = 'var(--port-selected-bd)'; // orange: used by child PDU strip
  } else if (pendingOutlet) {
    bg = 'var(--bg-toolbar)'; border = 'var(--slate-300)'; cursor = 'default';
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(outlet, source)}
      onKeyDown={e => e.key === 'Enter' && onSelect(outlet, source)}
      title={
        conn
          ? `Conectado → ${conn.consumer_name} · ${conn.psu_label || conn.psu_component_model || 'PSU'} · ${conn.cable_type || ''} ${conn.cable_color || ''}`
          : childPduId
          ? `Alimenta regleta hija: ${childPduName} — clic para desconectar`
          : isCurrentlyPending
          ? 'Enchufe seleccionado — elige un PSU o conecta una regleta hija'
          : 'Libre — clic para iniciar conexión'
      }
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 10px', marginBottom: 3, borderRadius: 6,
        border: `2px solid ${border}`, background: bg,
        cursor, transition: 'border-color 0.12s, background 0.12s',
        outline: 'none', userSelect: 'none',
      }}
    >
      {/* Number badge */}
      <span style={{
        minWidth: 26, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700,
        color: 'var(--slate-700)', background: 'rgba(255,255,255,.12)', borderRadius: 4, padding: '1px 4px',
      }}>
        #{outlet.outlet_number}
      </span>

      {/* Outlet type */}
      <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: 48 }}>
        {outlet.outlet_type || 'C13'}
      </span>

      {conn ? (
        <>
          <span style={{ color: colorHex(conn.cable_color), fontSize: '0.9rem', lineHeight: 1 }}>●</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--green-600)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {conn.consumer_name}
          </span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {conn.psu_label || conn.psu_component_model || 'PSU'}
          </span>
          <span style={{ fontSize: '0.75rem' }}>✂️</span>
        </>
      ) : childPduId ? (
        <>
          <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>🔌</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--status-degraded)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
            ↳ {childPduName}
          </span>
          <span style={{ fontSize: '0.67rem', color: 'var(--status-degraded)', background: 'var(--amber-50)', padding: '1px 5px', borderRadius: 4, whiteSpace: 'nowrap' }}>
            Regleta hija
          </span>
          <span style={{ fontSize: '0.75rem' }}>✂️</span>
        </>
      ) : isCurrentlyPending ? (
        <>
          <span style={{ fontSize: '0.8rem', color: 'var(--port-selected-bd)' }}>→</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--status-degraded)', flex: 1, fontWeight: 600 }}>Seleccionado — elige PSU o regleta hija</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Esc para cancelar</span>
        </>
      ) : (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flex: 1 }}>Libre</span>
      )}
    </div>
  );
}

/** Single PSU row in a device card */
function PsuRow({ psu, consumerId, consumerName, consumerType, connectedFrom, pendingOutlet, onSelect }) {
  const isConnected = Boolean(connectedFrom);

  let bg     = 'var(--bg-toolbar)';
  let border = 'var(--slate-300)';
  let cursor = 'pointer';

  if (isConnected) {
    const cHex = colorHex(connectedFrom.cable_color);
    bg = `${cHex}18`;
    border = cHex;
  } else if (pendingOutlet) {
    bg = 'var(--green-50)'; border = 'var(--port-connected-dot)'; cursor = 'crosshair';
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(psu, consumerId, consumerName, consumerType)}
      onKeyDown={e => e.key === 'Enter' && onSelect(psu, consumerId, consumerName, consumerType)}
      title={
        isConnected
          ? `Alimentado desde ${connectedFrom.pdu_name} enchufe #${connectedFrom.outlet_number}`
          : pendingOutlet
          ? 'Clic para conectar cable aquí'
          : 'Sin alimentación'
      }
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '5px 10px', marginBottom: 3, borderRadius: 6,
        border: `2px solid ${border}`, background: bg,
        cursor, transition: 'border-color 0.12s, background 0.12s',
        outline: 'none', userSelect: 'none',
      }}
    >
      <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>⚡</span>

      <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {psu.label || psu.model || `PSU ${psu.id}`}
        {psu.manufacturer && <span style={{ color: 'var(--text-muted)' }}> · {psu.manufacturer}</span>}
      </span>

      {isConnected ? (
        <>
          <span style={{ color: colorHex(connectedFrom.cable_color), fontSize: '0.9rem' }}>●</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--green-600)', whiteSpace: 'nowrap' }}>
            {connectedFrom.pdu_name} #{connectedFrom.outlet_number}
          </span>
          <span style={{ fontSize: '0.75rem' }}>✂️</span>
        </>
      ) : pendingOutlet ? (
        <span style={{ fontSize: '0.72rem', color: 'var(--conn-arrow)', fontWeight: 600, whiteSpace: 'nowrap' }}>← Conectar aquí</span>
      ) : (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Sin alimentación</span>
      )}
    </div>
  );
}

/** Mini form shown when user clicks outlet + PSU */
function ConnectForm({ outlet, psu, consumerId, consumerName, consumerType, sourceKind, upsId, companyId, onSave, onCancel }) {
  const [form, setForm] = useState({
    cable_type:  'IEC C13/C14',
    cable_color: 'negro',
    notes:       '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        consumer_type:    consumerType,
        consumer_id:      consumerId,
        psu_component_id: psu.id,
        psu_label:        psu.label || psu.model || null,
        cable_type:       form.cable_type  || null,
        cable_color:      form.cable_color || null,
        notes:            form.notes       || null,
      };
      if (sourceKind === 'ups') {
        await api.connectUpsOutlet(upsId, outlet.id, payload, companyId);
      } else {
        await api.createPowerConnection({ ...payload, outlet_id: outlet.id }, companyId);
      }
      onSave();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem',
        width: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', border: '1px solid var(--slate-300)',
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          🔌 Conectar cable de alimentación
        </h3>

        {/* Summary box */}
        <div style={{
          background: 'var(--bg-input)', borderRadius: 8, padding: '0.65rem 0.9rem',
          marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.6,
          border: '1px solid var(--border)',
        }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Desde:</span> <strong>{outlet.sourceName || outlet.pduName}</strong> · enchufe <strong>#{outlet.outlet_number}</strong> ({outlet.outlet_type || 'C13'})</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Hacia:</span> <strong>{consumerName}</strong> · {psu.label || psu.model || `PSU ${psu.id}`}</div>
        </div>

        {error && (
          <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {/* Cable type */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Tipo de cable
        </label>
        <select
          value={form.cable_type}
          onChange={e => setForm({ ...form, cable_type: e.target.value })}
          style={{ width: '100%', marginBottom: '1rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--slate-300)', fontSize: '0.85rem', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
        >
          {CABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {/* Cable color */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
          Color del cable
        </label>
        <div style={{ display: 'flex', gap: 7, marginBottom: '1rem', flexWrap: 'wrap' }}>
          {CABLE_COLORS.map(c => (
            <button
              key={c.value}
              onClick={() => setForm({ ...form, cable_color: c.value })}
              title={c.label}
              style={{
                width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                background: c.hex,
                border: form.cable_color === c.value
                  ? '3px solid var(--accent)'
                  : c.value === 'blanco' ? '2px solid var(--border)' : '2px solid transparent',
                boxShadow: form.cable_color === c.value ? '0 0 0 2px rgba(var(--accent-rgb),.35)' : 'none',
                transition: 'box-shadow 0.12s',
              }}
            />
          ))}
        </div>

        {/* Notes */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Notas <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(opcional)</span>
        </label>
        <input
          type="text"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Ej: Cable PDU-A → Servidor principal PSU-1"
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          style={{ width: '100%', marginBottom: '1.25rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--slate-300)', fontSize: '0.85rem', boxSizing: 'border-box', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none',
              background: saving ? 'var(--accent-l)' : 'var(--accent)',
              color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem',
            }}
          >
            {saving ? 'Guardando…' : '🔌 Conectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Disconnect confirmation popup */
function DisconnectConfirm({ conn, companyId, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const confirmDisconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.deletePowerConnection(conn.id, companyId);
      onDone();
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem',
        width: 360, boxShadow: '0 24px 64px rgba(0,0,0,0.6)', border: '1px solid var(--slate-300)',
      }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>✂️ Desconectar cable</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>
          ¿Desconectar el cable entre <strong>{conn.pdu_name}</strong>{' '}
          enchufe <strong>#{conn.outlet_number}</strong> y{' '}
          <strong>{conn.consumer_name}</strong>?
        </p>
        {conn.cable_type && (
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 1rem' }}>
            Cable: {conn.cable_type}
            {conn.cable_color && (
              <>
                {' '}·{' '}
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: colorHex(conn.cable_color), verticalAlign: 'middle' }} />
                {' '}{conn.cable_color}
              </>
            )}
          </p>
        )}
        {error && (
          <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}
          >
            Cancelar
          </button>
          <button
            onClick={confirmDisconnect}
            disabled={loading}
            style={{
              padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none',
              background: loading ? 'var(--error-bd)' : 'var(--btn-danger)',
              color: '#fff', fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem',
            }}
          >
            {loading ? 'Desconectando…' : '✂️ Desconectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DaisyChainForm ────────────────────────────────────────────────────────────

/**
 * Popup form to connect a free outlet of a parent PDU to a child PDU strip
 * (daisy-chain / cascaded PDU scenario).
 *
 * Props:
 *  outlet      – { id, outlet_number, outlet_type, pduName }
 *  availablePdus – list of PDU objects that have no parent_pdu_id yet
 *  companyId
 *  onSave      – called after successful update
 *  onCancel
 */
function DaisyChainForm({ outlet, availablePdus, companyId, onSave, onCancel }) {
  const [selectedPduId, setSelectedPduId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const handleSave = async () => {
    if (!selectedPduId) { setError('Selecciona una regleta hija.'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.updatePdu(Number(selectedPduId), { upstream_outlet_id: outlet.id }, companyId);
      onSave();
    } catch (e) {
      setError(e.message || 'Error al conectar la regleta hija.');
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem',
        width: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          🔌 Conectar regleta hija (daisy-chain)
        </h3>

        {/* Outlet summary */}
        <div style={{
          background: 'var(--warn-bg)', border: '1px solid var(--warn-bd)', borderRadius: 8, padding: '0.65rem 0.9rem',
          marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.6,
        }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Enchufe:</span>{' '}
            <strong>{outlet.sourceName || outlet.pduName}</strong> · #{outlet.outlet_number}{' '}
            <span style={{ color: 'var(--warn-text)' }}>({outlet.outlet_type || 'C13'})</span>
          </div>
          <div style={{ marginTop: 2, fontSize: '0.77rem', color: 'var(--warn-text)' }}>
            Este enchufe alimentará la entrada de la regleta hija seleccionada.
          </div>
        </div>

        {error && (
          <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {/* PDU selector */}
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Regleta hija a conectar
        </label>
        {availablePdus.length === 0 ? (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', padding: '0.5rem 0.75rem', background: 'var(--bg-toolbar)', borderRadius: 6, marginBottom: '1rem' }}>
            No hay regletas disponibles para conectar (todas tienen ya una PDU padre).
          </div>
        ) : (
          <select
            value={selectedPduId}
            onChange={e => setSelectedPduId(e.target.value)}
            style={{ width: '100%', marginBottom: '1.25rem', padding: '0.45rem 0.6rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.85rem' }}
          >
            <option value="">— Selecciona una regleta —</option>
            {availablePdus.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.rack_name ? ` (${p.rack_name})` : ''}
              </option>
            ))}
          </select>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedPduId}
            style={{
              padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none',
              background: saving || !selectedPduId ? 'var(--green-50)' : 'var(--status-degraded)',
              color: '#fff', fontWeight: 700,
              cursor: saving || !selectedPduId ? 'not-allowed' : 'pointer', fontSize: '0.85rem',
            }}
          >
            {saving ? 'Conectando…' : '🔌 Conectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DaisyChainDisconnectConfirm ───────────────────────────────────────────────

/**
 * Confirm popup to remove PDU→PDU (daisy-chain) connection.
 *
 * Props:
 *  outletNumber   – parent outlet number (for display)
 *  parentPduName  – name of the parent PDU (for display)
 *  childPduId     – id of the child PDU to update
 *  childPduName   – name of the child PDU (for display)
 *  companyId
 *  onDone         – called after successful update
 *  onCancel
 */
function DaisyChainDisconnectConfirm({ outletNumber, parentPduName, sourceKind, upsId, outletId, childPduId, childPduName, companyId, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const confirmDisconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      if (sourceKind === 'ups' && upsId && outletId) {
        await api.disconnectUpsOutlet(upsId, outletId, companyId);
      } else {
        await api.updatePdu(childPduId, { upstream_outlet_id: null, parent_pdu_id: null }, companyId);
      }
      onDone();
    } catch (e) {
      setError(e.message || 'Error al desconectar la regleta hija.');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14, padding: '1.75rem',
        width: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
      }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>
          ✂️ Desconectar regleta hija
        </h3>
        <div style={{
          background: 'var(--error-bg)', border: '1px solid var(--error-bd)', borderRadius: 8, padding: '0.65rem 0.9rem',
          marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.6,
        }}>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Enchufe padre:</span>{' '}
            <strong>{parentPduName}</strong> · #{outletNumber}
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Regleta hija:</span>{' '}
            <strong>{childPduName}</strong>
          </div>
          <div style={{ marginTop: 4, fontSize: '0.77rem', color: 'var(--error-text)' }}>
            Se eliminará el cascado. La regleta hija quedará sin alimentación asignada.
          </div>
        </div>

        {error && (
          <div style={{ color: 'var(--error-text)', fontSize: '0.8rem', marginBottom: '0.75rem', background: 'var(--error-bg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ padding: '0.5rem 1.1rem', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}
          >
            Cancelar
          </button>
          <button
            onClick={confirmDisconnect}
            disabled={loading}
            style={{
              padding: '0.5rem 1.3rem', borderRadius: 7, border: 'none',
              background: loading ? 'var(--error-bd)' : 'var(--btn-danger)',
              color: '#fff', fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem',
            }}
          >
            {loading ? 'Desconectando…' : '✂️ Desconectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Diagram view ─────────────────────────────────────────────────────────────

/**
 * SVG wiring diagram. Shows a left column (PDU outlets with connections) and a
 * right column (Server PSUs with connections) with bezier curves between them.
 */
function DiagramView({ pdus, consumers, consumerPsus, psuConnectionMap, onDisconnect, onDisconnectDaisy }) {
  const containerRef = useRef(null);
  const leftColEl    = useRef(null);
  const rightColEl   = useRef(null);
  const leftRefs     = useRef({});   // { outletId: domNode }
  const rightRefs    = useRef({});   // { psuId: domNode } + { 'pdu-<id>': domNode }
  const [cables,  setCables]  = useState([]);
  const [tooltip, setTooltip] = useState(null);

  // Build connection map from passed pdus (respects PDU filter from toolbar)
  const localPsuMap = useMemo(() => {
    const map = {};
    pdus.forEach(pdu => {
      (pdu.outlets || []).forEach(outlet => {
        const conn = outlet.connection;
        if (conn?.psu_component_id) {
          map[conn.psu_component_id] = { ...conn, outlet_number: outlet.outlet_number, pdu_name: pdu.name };
        }
      });
    });
    return map;
  }, [pdus]);

  // Memoize connection list so useLayoutEffect dep is stable
  // Includes both PSU connections and PDU→PDU daisy-chain links
  const connections = useMemo(() => {
    const list = [];
    pdus.forEach(pdu => {
      (pdu.outlets || []).forEach(outlet => {
        const conn = outlet.connection;
        if (conn?.psu_component_id) {
          list.push({ outletId: outlet.id, psuId: conn.psu_component_id, conn, kind: 'psu' });
        }
        if (outlet.child_pdu_id) {
          list.push({
            outletId:   outlet.id,
            psuId:      `pdu-${outlet.child_pdu_id}`,
            conn:       { cable_color: 'naranja', outlet_number: outlet.outlet_number, pdu_name: pdu.name, child_pdu_name: outlet.child_pdu_name },
            kind:       'daisy',
            childPduId: outlet.child_pdu_id,
            outletNumber: outlet.outlet_number,
            parentPduName: pdu.name,
            sourceKind: pdu.kind,
            upsId: pdu.upsId,
            daisyOutletId: outlet.id,
            childPduName: outlet.child_pdu_name,
          });
        }
      });
    });
    return list;
  }, [pdus]);

  // Compute cable positions — called both after DOM renders and on column scroll
  const measurePositions = useCallback(() => {
    if (!containerRef.current) return;
    const cRect = containerRef.current.getBoundingClientRect();
    const newCables = connections.map(({ outletId, psuId, conn, kind, childPduId, outletNumber, parentPduName, sourceKind, upsId, daisyOutletId, childPduName }) => {
      const lEl = leftRefs.current[outletId];
      const rEl = rightRefs.current[psuId];
      if (!lEl || !rEl) return null;
      const lr = lEl.getBoundingClientRect();
      const rr = rEl.getBoundingClientRect();
      return {
        outletId, psuId,
        x1: lr.right - cRect.left,
        y1: lr.top   - cRect.top + lr.height / 2,
        x2: rr.left  - cRect.left,
        y2: rr.top   - cRect.top + rr.height / 2,
        conn, kind, childPduId, outletNumber, parentPduName, sourceKind, upsId, daisyOutletId, childPduName,
      };
    }).filter(Boolean);
    setCables(newCables);
  }, [connections]);

  useLayoutEffect(() => { measurePositions(); }, [measurePositions]);

  // Re-measure on column scroll so cables follow scrolled elements
  useEffect(() => {
    const l = leftColEl.current;
    const r = rightColEl.current;
    if (l) l.addEventListener('scroll', measurePositions, { passive: true });
    if (r) r.addEventListener('scroll', measurePositions, { passive: true });
    return () => {
      if (l) l.removeEventListener('scroll', measurePositions);
      if (r) r.removeEventListener('scroll', measurePositions);
    };
  }, [measurePositions]);

  const colH = 'calc(100vh - 260px)';

  // Child PDUs (those with upstream_outlet_id set) — shown in right column
  const childPdus = pdus.filter(p => p.parent_pdu_id || p.upstream_outlet_id);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'flex', gap: 0, height: colH, overflow: 'hidden' }}
    >
      {/* ── Left column: connected outlets ── */}
      <div ref={leftColEl} style={{ width: '28%', overflowY: 'auto', height: '100%', paddingRight: 8, flexShrink: 0, zIndex: 1 }}>
        {pdus.map(pdu => {
          const connectedOutlets = (pdu.outlets || []).filter(o => o.connection?.psu_component_id || o.child_pdu_id);
          if (connectedOutlets.length === 0) return null;
          return (
            <div key={pdu.id} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                {pdu.kind === 'ups' ? '🔋 ' : ''}{pdu.name}
                {pdu.kind === 'ups' && (
                  <span style={{ marginLeft: 5, fontSize: '0.62rem', color: '#b45309', background: 'rgba(245,158,11,.16)', padding: '0 5px', borderRadius: 4 }}>SAI</span>
                )}
              </div>
              {connectedOutlets.map(outlet => {
                const isDaisy = Boolean(outlet.child_pdu_id);
                const borderColor = isDaisy ? 'var(--status-degraded)' : colorHex(outlet.connection?.cable_color);
                return (
                  <div
                    key={outlet.id}
                    ref={el => { leftRefs.current[outlet.id] = el; }}
                    style={{
                      background: isDaisy ? 'var(--warn-bg)' : 'var(--bg-card)',
                      border: `2px solid ${borderColor}`,
                      borderRadius: 6, padding: '4px 8px', marginBottom: 4, fontSize: '0.75rem',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {isDaisy
                      ? <span style={{ fontSize: '0.8rem' }}>🔌</span>
                      : <span style={{ color: borderColor, fontWeight: 700 }}>●</span>
                    }
                    <span style={{ fontWeight: 600 }}>#{outlet.outlet_number}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{outlet.outlet_type || 'C13'}</span>
                    {isDaisy && (
                      <span style={{ fontSize: '0.68rem', color: 'var(--warn-text)', marginLeft: 2 }}>↳ PDU</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── Middle: SVG cables (absolutely positioned, not blocking columns) ── */}
      <svg
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          overflow: 'hidden', zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <defs>
          {CABLE_COLORS.map(c => (
            <marker
              key={c.value}
              id={`arrow-${c.value}`}
              markerWidth="7" markerHeight="7"
              refX="6" refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 7 3.5, 0 7" fill={c.hex} />
            </marker>
          ))}
          <marker id="arrow-default" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0, 7 3.5, 0 7" fill="#9ca3af" />
          </marker>
          {/* Orange arrowhead for daisy-chain PDU→PDU cables */}
          <marker id="arrow-daisy" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0, 7 3.5, 0 7" fill="#f97316" />
          </marker>
        </defs>

        {cables.map(({ outletId, psuId, x1, y1, x2, y2, conn, kind, childPduId, outletNumber, parentPduName, sourceKind, upsId, daisyOutletId, childPduName }) => {
          const isDaisy  = kind === 'daisy';
          const cHex     = isDaisy ? '#f97316' : colorHex(conn.cable_color);
          const markerId = isDaisy ? 'arrow-daisy' : (conn.cable_color ? `arrow-${conn.cable_color}` : 'arrow-default');
          const midX     = (x1 + x2) / 2;
          const d        = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
          const tooltipText = isDaisy
            ? `PDU cascada: ${parentPduName} #${outletNumber} → ${childPduName}`
            : `${conn.pdu_name || ''} #${conn.outlet_number} → ${conn.consumer_name}\n${conn.psu_label || 'PSU'} · ${conn.cable_type || ''} ${conn.cable_color || ''}`.trim();
          return (
            <g key={`${outletId}-${psuId}`} style={{ pointerEvents: 'auto' }}>
              {/* Wide invisible hit area */}
              <path d={d} stroke="transparent" strokeWidth={16} fill="none"
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => {
                  setTooltip({ clientX: e.clientX, clientY: e.clientY, text: tooltipText, isDaisy });
                }}
                onMouseMove={e => {
                  setTooltip(prev => prev ? { ...prev, clientX: e.clientX, clientY: e.clientY } : prev);
                }}
                onMouseLeave={() => setTooltip(null)}
                onClick={() => {
                  setTooltip(null);
                  if (isDaisy) {
                    onDisconnectDaisy({ outletNumber, parentPduName, sourceKind, upsId, outletId: daisyOutletId, childPduId, childPduName });
                  } else {
                    onDisconnect(conn);
                  }
                }}
              />
              {/* Visible cable */}
              <path
                d={d}
                stroke={cHex}
                strokeWidth={2.5}
                fill="none"
                markerEnd={`url(#${markerId})`}
                strokeDasharray={conn.cable_type === 'IEC C19/C20' ? '6 3' : undefined}
                style={{ pointerEvents: 'none' }}
              />
            </g>
          );
        })}
      </svg>

      {/* ── Right column: connected PSUs ── */}
      <div ref={rightColEl} style={{ width: '28%', overflowY: 'auto', height: '100%', paddingLeft: 8, marginLeft: 'auto', flexShrink: 0, zIndex: 1 }}>
        {/* Server PSU nodes */}
        {consumers.map(server => {
          const psus = consumerPsus[server.id] || [];
          const connectedPsus = psus.filter(p => localPsuMap[p.id]);
          if (connectedPsus.length === 0) return null;
          return (
            <div key={`${server.consumerType}:${server.id}`} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.05em' }}>
                {server.name}
              </div>
              {connectedPsus.map(psu => {
                const conn = localPsuMap[psu.id];
                return (
                  <div
                    key={psu.id}
                    ref={el => { rightRefs.current[psu.id] = el; }}
                    style={{
                      background: 'var(--bg-toolbar)', border: `2px solid ${colorHex(conn.cable_color)}`,
                      borderRadius: 6, padding: '4px 8px', marginBottom: 4, fontSize: '0.75rem',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <span style={{ color: colorHex(conn.cable_color), fontWeight: 700 }}>●</span>
                    <span style={{ fontWeight: 600 }}>{psu.label || psu.model || `PSU ${psu.id}`}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Child PDU nodes (daisy-chain targets) */}
        {childPdus.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--warn-text)', textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 5 }}>
              🔌 Regletas hija (cascadas)
            </div>
            {childPdus.map(pdu => (
              <div
                key={`pdu-${pdu.id}`}
                ref={el => { rightRefs.current[`pdu-${pdu.id}`] = el; }}
                style={{
                  background: 'var(--warn-bg)', border: '2px solid var(--status-degraded)',
                  borderRadius: 6, padding: '4px 8px', marginBottom: 6, fontSize: '0.75rem',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <span style={{ fontSize: '0.85rem' }}>🔌</span>
                <span style={{ fontWeight: 700, color: 'var(--warn-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pdu.name}</span>
                {pdu.rack_name && (
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{pdu.rack_name}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.clientX + 14, top: tooltip.clientY - 14, zIndex: 3000,
          background: 'rgba(17,24,39,0.92)', color: '#fff', fontSize: '0.75rem',
          padding: '6px 10px', borderRadius: 6, pointerEvents: 'none',
          maxWidth: 320, whiteSpace: 'pre-wrap', lineHeight: 1.5,
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          {tooltip.text}
          <div style={{ marginTop: 3, color: 'var(--error-bd)', fontSize: '0.7rem' }}>Clic para desconectar ✂️</div>
        </div>
      )}

      {connections.length === 0 && (
        <div style={{
          width: '100%', padding: '4rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', fontSize: '0.9rem',
        }}>
          No hay conexiones de alimentación registradas aún.
        </div>
      )}
    </div>
  );
}

// ── Tarjeta de fuente para la vista de lista ─────────────────────────────────

function SourceListCard({ source, selectedOutlet, onOutletSelect }) {
  const isUps = source.kind === 'ups';
  const used  = source.outlets_used || 0;
  const total = source.outlets_total || (source.outlets || []).length;
  const pct   = total > 0 ? Math.round((used / total) * 100) : 0;
  const statusColor = pct === 100
    ? 'var(--red-600)'
    : pct > 0
    ? (isUps ? '#f59e0b' : 'var(--amber-600)')
    : 'var(--green-600)';

  return (
    <div
      style={{
        background: 'var(--bg-toolbar)',
        border: `1px solid ${isUps ? 'rgba(245,158,11,0.45)' : 'var(--border)'}`,
        borderRadius: 10,
        padding: '1rem',
        marginBottom: '1rem',
        boxShadow: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{isUps ? '🔋' : '🔌'}</span>
            {source.name}
            <span style={{
              fontSize: '0.62rem',
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: 4,
              background: isUps ? 'rgba(245,158,11,0.18)' : 'var(--bg-input)',
              color: isUps ? '#b45309' : 'var(--text-secondary)',
            }}>
              {isUps ? 'SAI' : 'PDU'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
            {source.rack_name ? `Rack: ${source.rack_name} · ` : ''}
            {(source.manufacturer || source.vendor) ? `${source.manufacturer || source.vendor} · ` : ''}
            {used}/{total} tomas usadas
          </div>
        </div>
        <div style={{
          fontSize: '0.7rem', fontWeight: 700, color: '#fff', padding: '2px 9px', borderRadius: 10,
          background: statusColor,
        }}>
          {pct === 100 ? 'LLENA' : pct > 0 ? 'PARCIAL' : 'LIBRE'}
        </div>
      </div>

      {(source.outlets || []).length === 0 ? (
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0, textAlign: 'center', padding: '0.5rem 0' }}>
          Sin tomas — añádelas desde la ficha {isUps ? 'del SAI' : 'de la PDU'}.
        </p>
      ) : (
        (source.outlets || []).map(outlet => (
          <OutletRow
            key={outlet.id}
            outlet={outlet}
            source={source}
            isSelected={selectedOutlet?.id === outlet.id}
            pendingOutlet={selectedOutlet}
            onSelect={onOutletSelect}
          />
        ))
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PowerCanvas({ companyId }) {
  const [pdus,             setPdus]             = useState([]);   // fuentes PDU
  const [sais,             setSais]             = useState([]);   // fuentes SAI
  const [consumers,        setConsumers]        = useState([]);   // all device types
  const [consumerPsus,     setConsumerPsus]     = useState({});   // { consumerId: [psu, ...] }
  const [locations,        setLocations]        = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState(null);
  const [psusLoading,      setPsusLoading]      = useState(false);
  const [view,             setView]             = useState('list'); // 'list' | 'grid' | 'diagram' | 'map'
  const [showOnlyConnected, setShowOnlyConnected] = useState(false);
  const [showSelectors,    setShowSelectors]    = useState(false);
  const [selectedTypes,    setSelectedTypes]    = useState({
    'Servidor': true, 'Switch': true, 'Router': true,
    'Cabina': true, 'Chasis': true, 'Firewall': true,
  });

  // Interaction state
  const [selectedOutlet,     setSelectedOutlet]     = useState(null);  // { ...outlet, sourceKind, upsId, sourceName }
  const [connectingPsu,      setConnectingPsu]      = useState(null);  // { psu, consumerId, consumerName, consumerType }
  const [disconnecting,      setDisconnecting]      = useState(null);  // conn object (from outlet.connection)
  const [connectingDaisy,    setConnectingDaisy]    = useState(null);  // { ...outlet, sourceName }
  const [disconnectingDaisy, setDisconnectingDaisy] = useState(null);  // { outletNumber, sourceName, ... }

  // Toolbar extras
  const [selSourceKey, setSelSourceKey] = useState('');    // Source filter (PDU/SAI)
  const [searchText, setSearchText] = useState('');    // Device name search (right column)
  const [maximized,  setMaximized]  = useState(false); // Fullscreen overlay

  // Filtro por ubicación (se recuerda entre visitas)
  const [locationFilter, setLocationFilter] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('cmdb.power.locations') || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch { return []; }
  });

  // ── data loading ───────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch PDUs, SAIs, ubicaciones y todos los tipos de equipo
      const [
        pduListRes, serverRes, switchRes, routerRes,
        storageRes, chassisRes, firewallRes, upsRes, locRes,
      ] = await Promise.all([
        api.getPdus(companyId),
        api.getServers(companyId),
        api.getElements(companyId, { element_type: 'switch' }),
        api.getRouters(companyId),
        api.getStorageArrays(companyId),
        api.getChassis({ company_id: companyId }),
        api.getFirewalls(companyId),
        api.getUpsList(companyId).catch(() => ({ data: [] })),
        api.getLocations('', companyId).catch(() => ({ data: [] })),
      ]);

      setLocations(locRes.data || []);

      const pduBasic = pduListRes.data || [];

      // 2. Normalize all device types to a unified consumer format
      const normalize = (list, consumerType, deviceLabel, deviceIcon, subLabelFn) =>
        (list || []).map(d => ({
          id:           d.id,
          key:          `${consumerType}:${d.id}`,
          name:         d.name,
          consumerType,
          deviceLabel,
          deviceIcon,
          subLabel:     subLabelFn(d),
          locationId:   d.location_id ?? null,
        }));

      const allConsumers = [
        ...normalize(serverRes.data,  'server',       'Servidor', '🖥️', d => d.hostname || d.rack_name || ''),
        ...normalize(switchRes.data,  'rack_element', 'Switch',   '🔀', d => d.rack_name || ''),
        ...((routerRes.data || [])
          .filter(r => r.rack_element_id)
          .map(r => ({
            id: r.rack_element_id,
            key: `rack_element:${r.rack_element_id}`,
            name: r.name,
            consumerType: 'rack_element',
            deviceLabel: 'Router',
            deviceIcon: '🌐',
            subLabel: r.hostname || r.rack_name || '',
            locationId: r.location_id ?? null,
          }))),
        ...normalize(storageRes.data, 'rack_element', 'Cabina',   '💾', d => d.rack_name || d.storage_type || ''),
        ...normalize(chassisRes.data, 'chassis',      'Chasis',   '📦', d => d.rack_name || d.chassis_type || ''),
        ...((firewallRes.data || [])
          .filter(f => f.deployment_type !== 'virtual' && f.rack_element_id)
          .map(f => ({
            id: f.rack_element_id,
            key: `rack_element:${f.rack_element_id}`,
            name: f.name,
            consumerType: 'rack_element',
            deviceLabel: 'Firewall',
            deviceIcon: '🛡️',
            subLabel: f.hostname || f.rack_name || '',
            locationId: f.location_id ?? null,
          }))),
      ];
      setConsumers(allConsumers);

      // 3. Fetch full PDU detail (with outlets + connections) in parallel
      const pduDetails = await Promise.all(
        pduBasic.map(p =>
          api.getPdu(p.id, companyId)
            .then(r => r.data)
            .catch(() => ({ ...p, outlets: [], outlets_used: 0, outlets_total: 0 }))
        )
      );
      setPdus(pduDetails.map(p => ({ ...p, key: `pdu:${p.id}`, kind: 'pdu' })));

      // 4. Fetch SAI detail (with output outlets + connections) in parallel
      const saiBasic = (upsRes.data || []).filter(
        u => u.rack_element_id && ((u.outlets_total || 0) > 0 || (u.outlets_count || 0) > 0)
      );
      const saiDetails = await Promise.all(
        saiBasic.map(async u => {
          const [res, inputRes] = await Promise.all([
            api.getUpsOutlets(u.id, companyId).catch(() => null),
            api.getPowerConnections('rack_element', u.rack_element_id, companyId).catch(() => null),
          ]);
          const outlets = res?.data?.outlets || [];
          return {
            key: `ups:${u.rack_element_id}`,
            kind: 'ups',
            id: u.rack_element_id,
            upsId: u.id,
            name: u.name,
            vendor: u.vendor,
            rack_name: u.rack_name,
            location_id: u.location_id ?? null,
            location_name: u.location_name,
            outlets,
            inputConnections: inputRes?.data || [],
            outlets_total: res?.data?.outlets_total ?? outlets.length,
            outlets_used: res?.data?.outlets_used ?? outlets.filter(o => o.connection || o.child_pdu_id).length,
            parent_pdu_id: null,
            upstream_outlet_id: null,
          };
        })
      );
      setSais(saiDetails);

      // 5. Fetch PSU components for all consumers in parallel
      setPsusLoading(true);
      const psuMap = {};
      await Promise.all(
        allConsumers.map(async (consumer) => {
          try {
            const res = consumer.consumerType === 'chassis'
              ? await api.getChassisComponents(consumer.id, companyId)
              : await api.getComponents(consumer.id, companyId);
            psuMap[consumer.id] = (res.data || []).filter(c => c.component_type === 'psu');
          } catch {
            psuMap[consumer.id] = [];
          }
        })
      );
      setConsumerPsus(psuMap);
    } catch (e) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
      setPsusLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Escape key → cancel selection
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setSelectedOutlet(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── derived data ──────────────────────────────────────────────────────────

  const allSources = useMemo(() => [...pdus, ...sais], [pdus, sais]);

  /**
   * psuConnectionMap: { psuComponentId → conn_enriched }
   * Lets PSU rows quickly check if they're already connected.
   */
  const psuConnectionMap = useMemo(() => {
    const map = {};
    allSources.forEach(source => {
      (source.outlets || []).forEach(outlet => {
        const conn = outlet.connection;
        if (conn?.psu_component_id) {
          map[conn.psu_component_id] = {
            ...conn,
            outlet_number: outlet.outlet_number,
            pdu_name:      source.name,
          };
        }
      });
    });
    return map;
  }, [allSources]);

  // Filtro por ubicación: afecta a fuentes y equipos.
  const filteredSources = useMemo(
    () => allSources.filter(source => matchesLocation(locations, locationFilter, source.location_id)),
    [allSources, locations, locationFilter]
  );
  const visibleSources = useMemo(
    () => (selSourceKey
      ? filteredSources.filter(source => source.key === selSourceKey)
      : filteredSources),
    [filteredSources, selSourceKey]
  );

  // Si el filtro de ubicación deja fuera la fuente elegida, se limpia.
  useEffect(() => {
    if (selSourceKey && !filteredSources.some(source => source.key === selSourceKey)) {
      setSelSourceKey('');
    }
  }, [selSourceKey, filteredSources]);

  // Stats (sobre lo visible)
  const totalOutlets  = visibleSources.reduce((s, src) => s + (src.outlets_total || 0), 0);
  const usedOutlets   = visibleSources.reduce((s, src) => s + (src.outlets_used  || 0), 0);
  const totalPsus     = Object.values(consumerPsus).reduce((s, arr) => s + arr.length, 0);
  const connectedPsus = Object.values(psuConnectionMap).length;

  // PDUs que pueden conectarse como hijas (sin padre), aunque el selector de
  // fuente esté mostrando un SAI.
  const availablePdus = useMemo(
    () => filteredSources.filter(source => source.kind === 'pdu' && !source.parent_pdu_id),
    [filteredSources]
  );

  // Filtered consumers (ubicación + selectores + solo conectados)
  const visibleConsumers = useMemo(
    () => consumers.filter(c => {
      if (!matchesLocation(locations, locationFilter, c.locationId)) return false;
      if (!selectedTypes[c.deviceLabel]) return false;
      if (searchText && !c.name.toLowerCase().includes(searchText.toLowerCase())) return false;
      if (showOnlyConnected) {
        const psus = consumerPsus[c.id] || [];
        return psus.some(p => psuConnectionMap[p.id]);
      }
      return true;
    }),
    [consumers, locations, locationFilter, selectedTypes, searchText, showOnlyConnected, consumerPsus, psuConnectionMap]
  );

  // All distinct device labels present
  const allDeviceLabels = useMemo(
    () => [...new Set(consumers.map(c => c.deviceLabel))],
    [consumers]
  );

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleOutletSelect = (outlet, source) => {
    const conn = outlet.connection;
    const sourceInfo = {
      sourceName: source?.name || '',
      sourceKind: source?.kind || 'pdu',
      upsId: source?.upsId,
      sourceKey: source?.key,
      elementId: source?.id,
    };
    if (conn) {
      // Show disconnect dialog (PSU connection)
      setSelectedOutlet(null);
      setDisconnecting({ ...conn, outlet_number: outlet.outlet_number, pdu_name: sourceInfo.sourceName });
    } else if (outlet.child_pdu_id) {
      // Outlet is used as upstream inlet for a child PDU → disconnect daisy-chain
      setSelectedOutlet(null);
      setDisconnectingDaisy({
        outletNumber:  outlet.outlet_number,
        parentPduName: sourceInfo.sourceName,
        sourceKind:    sourceInfo.sourceKind,
        upsId:         sourceInfo.upsId,
        outletId:      outlet.id,
        childPduId:    outlet.child_pdu_id,
        childPduName:  outlet.child_pdu_name,
      });
    } else if (selectedOutlet?.id === outlet.id) {
      // Deselect if clicking same outlet again
      setSelectedOutlet(null);
    } else {
      // Select as source
      setSelectedOutlet({ ...outlet, ...sourceInfo });
    }
  };

  const handlePsuSelect = (psu, consumerId, consumerName, consumerType) => {
    const existingConn = psuConnectionMap[psu.id];
    if (existingConn) {
      // Show disconnect dialog
      setDisconnecting(existingConn);
      setSelectedOutlet(null);
    } else if (selectedOutlet) {
      // Open connection form
      setConnectingPsu({ psu, consumerId, consumerName, consumerType });
    }
    // If no pending outlet and PSU is free, do nothing (user must select outlet first)
  };

  const handleConnectSaved = async () => {
    setConnectingPsu(null);
    setSelectedOutlet(null);
    await load();
  };

  const handleDisconnectDone = async () => {
    setDisconnecting(null);
    await load();
  };

  const handleDaisyChainSaved = async () => {
    setConnectingDaisy(null);
    setSelectedOutlet(null);
    await load();
  };

  const handleDaisyChainDisconnected = async () => {
    setDisconnectingDaisy(null);
    await load();
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (!companyId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        Selecciona una empresa para ver el canvas de alimentación.
      </div>
    );
  }

  return (
    <div style={maximized ? {
      position: 'fixed', inset: 0, zIndex: 1500, background: 'var(--bg-surface)',
      fontFamily: 'system-ui, sans-serif', overflow: 'auto', padding: '1.5rem 2rem',
    } : {
      padding: '1.5rem 2rem', fontFamily: 'system-ui, sans-serif', maxWidth: 1400,
    }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 10 }}>
            ⚡ Canvas de Alimentación
          </h1>
          <p style={{ margin: '0.3rem 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            PDUs y SAIs → Dispositivos · Gestión visual de cables de alimentación
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Stats pills */}
          {!loading && (
            <>
              <span style={{ fontSize: '0.75rem', background: 'var(--bg-toolbar)', padding: '4px 10px', borderRadius: 20, color: 'var(--text-secondary)' }}>
                🔌 {usedOutlets}/{totalOutlets} enchufes
              </span>
              <span style={{ fontSize: '0.75rem', background: 'var(--bg-toolbar)', padding: '4px 10px', borderRadius: 20, color: 'var(--text-secondary)' }}>
                ⚡ {connectedPsus}/{totalPsus} PSUs
              </span>
            </>
          )}

          {/* Filtro por ubicación */}
          <LocationFilter
            companyId={companyId}
            value={locationFilter}
            extraItems={locations}
            onChange={(ids) => {
              setLocationFilter(ids);
              try { localStorage.setItem('cmdb.power.locations', JSON.stringify(ids)); } catch { /* noop */ }
            }}
          />

          {/* Source (PDU/SAI) selector */}
          <select
            value={selSourceKey}
            onChange={e => setSelSourceKey(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.8rem', maxWidth: 220 }}
          >
            <option value="">— Todas las fuentes —</option>
            {filteredSources.map(source => (
              <option key={source.key} value={source.key}>
                {source.kind === 'ups' ? 'SAI · ' : 'PDU · '}{source.name} ({source.outlets_used || 0}/{source.outlets_total || 0})
              </option>
            ))}
          </select>

          {/* Device name search */}
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Buscar dispositivo…"
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.8rem', width: 160 }}
          />

          {/* Solo conectados */}
          <button
            onClick={() => setShowOnlyConnected(v => !v)}
            style={{
              padding: '0.4rem 0.85rem', borderRadius: 7, fontSize: '0.82rem', cursor: 'pointer',
              border: showOnlyConnected ? '1.5px solid var(--accent)' : '1px solid var(--slate-300)',
              background: showOnlyConnected ? 'rgba(var(--accent-rgb),.18)' : 'var(--bg-input)',
              color: showOnlyConnected ? 'var(--accent-l)' : 'var(--text-secondary)',
              fontWeight: showOnlyConnected ? 700 : 400,
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            🔍 Solo conectados
          </button>

          {/* View toggle */}
          <div style={{ display: 'flex', border: '1px solid var(--slate-300)', borderRadius: 7, overflow: 'hidden' }}>
            {[['grid','⊞ Cuadrícula'],['list','≡ Lista'],['diagram','⤢ Diagrama'],['map','🗺️ Mapa']].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '0.4rem 0.85rem', border: 'none', cursor: 'pointer', fontSize: '0.82rem',
                  fontWeight: view === v ? 700 : 400,
                  background: view === v ? 'var(--accent)' : 'var(--bg-input)',
                  color:      view === v ? '#fff'    : 'var(--text-secondary)',
                  transition: 'background 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Selectores */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSelectors(v => !v)}
              style={{
                padding: '0.4rem 0.85rem', borderRadius: 7, fontSize: '0.82rem', cursor: 'pointer',
                border: showSelectors ? '1.5px solid var(--wall-accent)' : '1px solid var(--slate-300)',
                background: showSelectors ? 'rgba(139,92,246,.18)' : 'var(--bg-input)',
                color: showSelectors ? 'var(--wall-banner-bd)' : 'var(--text-secondary)',
                fontWeight: showSelectors ? 700 : 400,
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              ⚙️ Selectores
            </button>
            {showSelectors && (
              <div style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 100,
                background: 'var(--bg-surface)', border: '1px solid var(--slate-300)', borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                padding: '0.85rem 1rem', minWidth: 180,
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                  Tipos de dispositivo
                </div>
                {allDeviceLabels.map(label => (
                  <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, cursor: 'pointer', fontSize: '0.83rem', color: 'var(--text-primary)' }}>
                    <input
                      type="checkbox"
                      checked={selectedTypes[label] !== false}
                      onChange={e => setSelectedTypes(prev => ({ ...prev, [label]: e.target.checked }))}
                      style={{ width: 14, height: 14, cursor: 'pointer' }}
                    />
                    {label}
                  </label>
                ))}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', gap: 6 }}>
                  <button onClick={() => setSelectedTypes(Object.fromEntries(allDeviceLabels.map(l => [l, true])))}
                    style={{ flex: 1, fontSize: '0.72rem', padding: '3px 0', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', color: 'var(--text-primary)' }}>
                    Todo
                  </button>
                  <button onClick={() => setSelectedTypes(Object.fromEntries(allDeviceLabels.map(l => [l, false])))}
                    style={{ flex: 1, fontSize: '0.72rem', padding: '3px 0', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', cursor: 'pointer', color: 'var(--text-primary)' }}>
                    Ninguno
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={load}
            disabled={loading}
            style={{
              padding: '0.45rem 0.9rem', borderRadius: 7, border: '1px solid var(--slate-300)',
              background: 'var(--bg-input)', cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {loading ? '⌛' : '↺'} Recargar
          </button>

          {/* Maximize / restore */}
          <button
            onClick={() => setMaximized(v => !v)}
            title={maximized ? 'Salir de pantalla completa' : 'Pantalla completa'}
            style={{
              padding: '0.45rem 0.7rem', borderRadius: 7, fontSize: '1rem', lineHeight: 1,
              border: maximized ? '1.5px solid var(--accent)' : '1px solid var(--border)',
              background: maximized ? 'rgba(var(--accent-rgb),.1)' : 'var(--bg-card)',
              color: maximized ? 'var(--accent)' : 'var(--text-primary)',
              cursor: 'pointer',
            }}
          >
            {maximized ? '⤡' : '⤢'}
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          color: 'var(--error-text)', padding: '0.75rem 1rem', background: 'var(--error-bg)',
          borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {/* ── Pending connection banner ── */}
      {selectedOutlet && (
        <div style={{
          background: 'rgba(var(--port-selected-bd-rgb,249,115,22),.12)', border: '2px solid var(--status-degraded)', borderRadius: 10,
          padding: '0.65rem 1rem', marginBottom: '1.2rem',
          display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '1.1rem' }}>🔌</span>
          <span style={{ flex: 1 }}>
            Conectando desde <strong>{selectedOutlet.sourceName || selectedOutlet.pduName}</strong> enchufe{' '}
            <strong>#{selectedOutlet.outlet_number}</strong>{' '}
            ({selectedOutlet.outlet_type || 'C13'}) —{' '}
            Selecciona un PSU o conecta una regleta hija
          </span>
          <button
            onClick={() => setConnectingDaisy({ ...selectedOutlet })}
            style={{
              padding: '0.25rem 0.7rem', borderRadius: 5,
              border: '1px solid var(--status-degraded)', background: 'var(--warn-bg)',
              cursor: 'pointer', fontSize: '0.8rem', color: 'var(--warn-text)', fontWeight: 600,
            }}
          >
            🔌 Conectar regleta hija
          </button>
          <button
            onClick={() => setSelectedOutlet(null)}
            style={{
              padding: '0.25rem 0.7rem', borderRadius: 5,
              border: '1px solid var(--status-degraded)', background: 'rgba(var(--port-selected-bd-rgb,249,115,22),.15)',
              cursor: 'pointer', fontSize: '0.8rem', color: 'var(--status-degraded)', fontWeight: 600,
            }}
          >
            ✕ Cancelar (Esc)
          </button>
        </div>
      )}

      {/* ── Legend ── */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: '1.25rem', fontSize: '0.75rem', color: 'var(--text-muted)',
      }}>
        <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Leyenda:</span>
        <span>🔌 = clic para desconectar</span>
        <span>○ gris = libre (clic para conectar)</span>
        <span>● color = cable conectado</span>
        <span>🔋 = SAI</span>
        {selectedOutlet && <span style={{ color: 'var(--status-degraded)', fontWeight: 600 }}>← Conectar aquí = disponible para conectar</span>}
      </div>

      {/* ── Main grid ── */}
      {loading ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: 240, color: 'var(--text-muted)', fontSize: '0.9rem', gap: 10,
        }}>
          <div style={{ width: 20, height: 20, border: '2px solid var(--slate-300)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          Cargando fuentes y dispositivos…
        </div>
      ) : view === 'map' ? (
        <PowerMapView
          sources={filteredSources}
          consumers={visibleConsumers}
          consumerPsus={consumerPsus}
          psuConnectionMap={psuConnectionMap}
          selectedOutlet={selectedOutlet}
          onOutletSelect={handleOutletSelect}
          onPsuSelect={handlePsuSelect}
          onDisconnect={conn => setDisconnecting(conn)}
          onDisconnectDaisy={setDisconnectingDaisy}
        />
      ) : view === 'diagram' ? (
        <DiagramView
          pdus={visibleSources}
          consumers={visibleConsumers}
          consumerPsus={consumerPsus}
          psuConnectionMap={psuConnectionMap}
          onDisconnect={conn => setDisconnecting(conn)}
          onDisconnectDaisy={({ outletNumber, parentPduName, sourceKind, upsId, outletId, childPduId, childPduName }) =>
            setDisconnectingDaisy({ outletNumber, parentPduName, sourceKind, upsId, outletId, childPduId, childPduName })
          }
        />
      ) : view === 'grid' ? (
        // ── Grid view: compact tiles ──
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>
          {/* LEFT: Fuentes (PDU / SAI) */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.85rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--border)' }}>
              <span style={{ fontSize: '1.1rem' }}>🔌</span>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Fuentes</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)', padding: '2px 8px', borderRadius: 10 }}>{visibleSources.length}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {visibleSources.map(source => {
                const used  = source.outlets_used || 0;
                const total = source.outlets_total || 0;
                const pct   = total > 0 ? Math.round((used / total) * 100) : 0;
                const isUps = source.kind === 'ups';
                const color = isUps
                  ? '#f59e0b'
                  : pct === 100 ? 'var(--red-600)' : pct > 0 ? 'var(--amber-600)' : 'var(--green-600)';
                return (
                  <div key={source.key} style={{
                    border: `2px solid ${color}`, borderRadius: 8, padding: '8px 10px',
                    minWidth: 110, background: 'var(--bg-toolbar)', boxShadow: 'none',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)', marginBottom: 2 }}>
                      {isUps ? '🔋 ' : ''}{source.name}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{used}/{total} tomas</div>
                    <div style={{ marginTop: 5, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.1)', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
              {visibleSources.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>No hay fuentes en la ubicación seleccionada.</p>}
            </div>
          </div>

          {/* RIGHT: Devices */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.85rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--border)' }}>
              <span style={{ fontSize: '1.1rem' }}>⚡</span>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Dispositivos</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)', padding: '2px 8px', borderRadius: 10 }}>{visibleConsumers.length}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {visibleConsumers.map(consumer => {
                const psus     = consumerPsus[consumer.id] || [];
                const connected = psus.filter(p => psuConnectionMap[p.id]).length;
                const hasAll   = psus.length > 0 && connected === psus.length;
                const hasSome  = connected > 0 && !hasAll;
                const color    = hasAll ? 'var(--green-600)' : hasSome ? 'var(--amber-600)' : psus.length === 0 ? 'var(--text-muted)' : 'rgba(255,255,255,.2)';
                return (
                  <div key={`${consumer.consumerType}-${consumer.id}`} style={{
                    border: `2px solid ${color}`, borderRadius: 8, padding: '8px 10px',
                    minWidth: 120, background: 'var(--bg-toolbar)', boxShadow: 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                      <span style={{ fontSize: '0.85rem' }}>{consumer.deviceIcon}</span>
                      <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>{consumer.name}</span>
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      {consumer.deviceLabel} · {connected}/{psus.length} PSU{psus.length !== 1 ? 's' : ''}
                    </div>
                    <div style={{ marginTop: 5, display: 'flex', gap: 3 }}>
                      {psus.map(psu => {
                        const conn = psuConnectionMap[psu.id];
                        return (
                          <span key={psu.id} title={conn ? `Conectado a ${conn.pdu_name} #${conn.outlet_number}` : 'Sin conexión'} style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: conn ? colorHex(conn.cable_color) : 'rgba(255,255,255,.15)',
                            display: 'inline-block', border: '1px solid rgba(255,255,255,.2)',
                          }} />
                        );
                      })}
                      {psus.length === 0 && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Sin PSUs</span>}
                    </div>
                  </div>
                );
              })}
              {visibleConsumers.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>No hay dispositivos visibles con los filtros actuales.</p>}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>

          {/* ── LEFT: Sources (PDU / SAI) ── */}
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: '0.85rem', paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg-card)',
            }}>
              <span style={{ fontSize: '1.1rem' }}>🔌</span>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Fuentes
              </h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)', padding: '2px 8px', borderRadius: 10 }}>
                {visibleSources.length}{selSourceKey ? ` / ${filteredSources.length}` : ''}
              </span>
            </div>

            {/* Scrollable source content */}
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
              {visibleSources.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
                  {filteredSources.length === 0
                    ? 'No hay PDUs ni SAIs en la ubicación seleccionada.'
                    : 'Ninguna fuente visible con el filtro actual.'}
                </p>
              ) : (
                visibleSources.map(source => (
                  <SourceListCard
                    key={source.key}
                    source={source}
                    selectedOutlet={selectedOutlet}
                    onOutletSelect={handleOutletSelect}
                  />
                ))
              )}
            </div>{/* end scroll wrapper */}
          </div>

          {/* ── RIGHT: All Devices ── */}
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: '0.85rem', paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 10, background: 'var(--bg-card)',
            }}>
              <span style={{ fontSize: '1.1rem' }}>⚡</span>
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Dispositivos
              </h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)', padding: '2px 8px', borderRadius: 10 }}>
                {visibleConsumers.length}{visibleConsumers.length !== consumers.length ? ` / ${consumers.length}` : ''}
              </span>
              {psusLoading && (
                <span style={{ fontSize: '0.72rem', color: 'var(--accent)' }}>cargando PSUs…</span>
              )}
            </div>

            {/* Scrollable device content */}
            <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>

            {visibleConsumers.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
                {consumers.length === 0
                  ? 'No hay dispositivos con PSU registrados en esta empresa.'
                  : 'Ningún dispositivo visible con los filtros actuales.'}
              </p>
            ) : (
              visibleConsumers.map(consumer => {
                const psus = consumerPsus[consumer.id];
                const isLoadingPsus = psus === undefined;

                return (
                  <div
                    key={`${consumer.consumerType}-${consumer.id}`}
                    style={{
                      background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 10,
                      padding: '1rem', marginBottom: '1rem',
                      boxShadow: 'none',
                      ...(selectedOutlet && (psus || []).some(p => !psuConnectionMap[p.id])
                        ? { borderColor: 'var(--conn-banner-bd)', boxShadow: '0 0 0 2px rgba(134,239,172,.25)' }
                        : {}),
                    }}
                  >
                    {/* Device header */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '0.9rem' }}>{consumer.deviceIcon}</span>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.92rem' }}>{consumer.name}</span>
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                          background: 'var(--bg-toolbar)', color: 'var(--text-secondary)',
                        }}>
                          {consumer.deviceLabel}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {consumer.subLabel ? `${consumer.subLabel} · ` : ''}
                        {isLoadingPsus
                          ? 'Cargando PSUs…'
                          : psus.length === 0
                          ? 'Sin PSUs registradas'
                          : `${psus.length} PSU(s)`}
                      </div>
                    </div>

                    {/* PSU rows */}
                    {isLoadingPsus ? (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', padding: '0.4rem 0' }}>
                        …
                      </div>
                    ) : psus.length === 0 ? (
                      <div style={{
                        fontSize: '0.78rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)',
                        padding: '0.5rem 0.75rem', borderRadius: 6, textAlign: 'center',
                      }}>
                        Sin componentes PSU. Añádelos desde el detalle del dispositivo.
                      </div>
                    ) : (
                      psus.map(psu => (
                        <PsuRow
                          key={psu.id}
                          psu={psu}
                          consumerId={consumer.id}
                          consumerName={consumer.name}
                          consumerType={consumer.consumerType}
                          connectedFrom={psuConnectionMap[psu.id] || null}
                          pendingOutlet={selectedOutlet}
                          onSelect={handlePsuSelect}
                        />
                      ))
                    )}
                  </div>
                );
              })
            )}
            </div>{/* end scroll wrapper */}
          </div>
        </div>
      )}

      {/* ── Modals ── */}

      {connectingPsu && selectedOutlet && (
        <ConnectForm
          outlet={selectedOutlet}
          psu={connectingPsu.psu}
          consumerId={connectingPsu.consumerId}
          consumerName={connectingPsu.consumerName}
          consumerType={connectingPsu.consumerType}
          sourceKind={selectedOutlet.sourceKind}
          upsId={selectedOutlet.upsId}
          companyId={companyId}
          onSave={handleConnectSaved}
          onCancel={() => setConnectingPsu(null)}
        />
      )}

      {disconnecting && (
        <DisconnectConfirm
          conn={disconnecting}
          companyId={companyId}
          onDone={handleDisconnectDone}
          onCancel={() => setDisconnecting(null)}
        />
      )}

      {connectingDaisy && (
        <DaisyChainForm
          outlet={connectingDaisy}
          availablePdus={availablePdus.filter(p => p.key !== connectingDaisy.sourceKey)}
          companyId={companyId}
          onSave={handleDaisyChainSaved}
          onCancel={() => setConnectingDaisy(null)}
        />
      )}

      {disconnectingDaisy && (
        <DaisyChainDisconnectConfirm
          outletNumber={disconnectingDaisy.outletNumber}
          parentPduName={disconnectingDaisy.parentPduName}
          sourceKind={disconnectingDaisy.sourceKind}
          upsId={disconnectingDaisy.upsId}
          outletId={disconnectingDaisy.outletId}
          childPduId={disconnectingDaisy.childPduId}
          childPduName={disconnectingDaisy.childPduName}
          companyId={companyId}
          onDone={handleDaisyChainDisconnected}
          onCancel={() => setDisconnectingDaisy(null)}
        />
      )}

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
